import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Minimize2, Globe, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/App';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Merhaba! Ben Ener Otomasyon yapay zeka asistanıyım. Size nasıl yardımcı olabilirim?',
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [lastQuery, setLastQuery] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const messagesEndRef = useRef(null);
  const lastRequestTime = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  useEffect(() => {
    // Generate UNIQUE session ID per browser tab/window
    try {
      // Try sessionStorage first (works on most browsers)
      const stored = sessionStorage.getItem('chat_session_id');
      if (stored) {
        setSessionId(stored);
        return;
      }
    } catch (error) {
      // SessionStorage not available
    }
    
    // Create truly unique session ID with timestamp + random + browser fingerprint
    try {
      const browserId = navigator.userAgent + navigator.language + screen.width + screen.height;
      const fingerprint = Array.from(browserId).reduce((hash, char) => {
        return ((hash << 5) - hash) + char.charCodeAt(0) | 0;
      }, 0);
      const newId = `session-${Date.now()}-${Math.abs(fingerprint)}-${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newId);
      
      // Try to save to sessionStorage if available
      try {
        sessionStorage.setItem('chat_session_id', newId);
      } catch (storageError) {
        // Could not save to sessionStorage
      }
    } catch (error) {
      // Fallback: simple random session ID
      const fallbackId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(fallbackId);
    }
  }, []);

  const handleSend = async (enableWebSearch = false) => {
    if (!inputMessage.trim() || isLoading || cooldownSeconds > 0) return;
    
    // Check if too soon after last request (minimum 1 second between requests)
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime.current;
    if (timeSinceLastRequest < 1000) {
      const waitTime = Math.ceil((1000 - timeSinceLastRequest) / 1000);
      const warningMessage = {
        type: 'bot',
        text: `Lütfen ${waitTime} saniye bekleyin.`,
        timestamp: new Date(),
        isWarning: true,
      };
      setMessages((prev) => [...prev, warningMessage]);
      return;
    }
    
    lastRequestTime.current = now;

    const currentQuery = inputMessage;
    const userMessage = {
      type: 'user',
      text: currentQuery,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setLastQuery(currentQuery);

    try {
      // Use native fetch API instead of axios (more reliable on mobile)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      // Add timestamp to prevent caching issues
      const url = `${API}/chat?t=${Date.now()}`;
      
      // Safely create JSON body (avoid circular references)
      const requestBody = {
        message: String(currentQuery || ''),
        session_id: String(sessionId || ''),
        enable_web_search: enableWebSearch === true ? true : false,
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();

      const botMessage = {
        type: 'bot',
        text: data.response || 'Yanıt alınamadı.',
        timestamp: new Date(),
        webSearchAvailable: data.web_search_available,
        sources: data.sources,
        query: currentQuery,
      };

      setMessages((prev) => [...prev, botMessage]);
      
      // Update session ID if returned
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        try {
          sessionStorage.setItem('chat_session_id', data.session_id);
        } catch (error) {
          // Could not update sessionStorage
        }
      }
    } catch (error) {
      let errorText = 'Üzgünüm, bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
      let cooldown = 0;
      let canRetry = false;

      // Handle fetch API errors
      if (error.name === 'AbortError') {
        errorText = 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
        cooldown = 3;
        canRetry = true;
      } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorText = 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
        cooldown = 3;
        canRetry = true;
      } else if (error.message.includes('HTTP 429')) {
        errorText = 'Çok fazla istek gönderdiniz. Lütfen 10 saniye bekleyip tekrar deneyin.';
        cooldown = 10;
        canRetry = true;
      } else if (error.message.includes('HTTP 500')) {
        errorText = 'Yapay zeka servisi geçici olarak yanıt veremiyor. Lütfen birkaç saniye bekleyip tekrar deneyin.';
        cooldown = 5;
        canRetry = true;
      } else if (error.message.includes('HTTP 503')) {
        errorText = 'Servis geçici olarak kullanılamıyor. Lütfen birkaç saniye bekleyin.';
        cooldown = 5;
        canRetry = true;
      } else if (error.message.includes('HTTP 504')) {
        errorText = 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
        cooldown = 3;
        canRetry = true;
      }

      // Set cooldown if needed
      if (cooldown > 0) {
        setCooldownSeconds(cooldown);
      }

      const errorMessage = {
        type: 'bot',
        text: errorText,
        timestamp: new Date(),
        isError: true,
        canRetry: canRetry,
        retryQuery: currentQuery,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWebSearch = async (query) => {
    setIsLoading(true);
    
    try {
      // Use native fetch API
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      // Add timestamp to prevent caching issues
      const url = `${API}/chat?t=${Date.now()}`;
      
      // Safely create JSON body
      const requestBody = {
        message: String(query || ''),
        session_id: String(sessionId || ''),
        enable_web_search: true,
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();

      const botMessage = {
        type: 'bot',
        text: data.response || 'Web araması sonucu alınamadı.',
        timestamp: new Date(),
        sources: data.sources,
        fromWebSearch: true,
      };

      setMessages((prev) => [...prev, botMessage]);
      
    } catch (error) {
      let errorText = 'Web araması sırasında bir hata oluştu.';
      if (error.name === 'AbortError') {
        errorText = 'Web araması zaman aşımına uğradı. Lütfen tekrar deneyin.';
      } else if (error.message.includes('Failed to fetch')) {
        errorText = 'İnternet bağlantınızı kontrol edin ve tekrar deneyin.';
      }
      
      const errorMessage = {
        type: 'bot',
        text: errorText,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = (query) => {
    if (!query || isLoading || cooldownSeconds > 0) return;
    setInputMessage(query);
    // Use setTimeout to ensure state update is processed before sending
    setTimeout(() => {
      setInputMessage('');
      // Remove the error message before retrying
      setMessages((prev) => {
        const filtered = prev.filter((msg, idx) => !(idx === prev.length - 1 && msg.isError));
        return [...filtered, { type: 'user', text: query, timestamp: new Date() }];
      });
      setIsLoading(true);
      setLastQuery(query);
      lastRequestTime.current = Date.now();

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      const url = `${API}/chat?t=${Date.now()}`;

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String(query),
          session_id: String(sessionId || ''),
          enable_web_search: false,
        }),
        signal: controller.signal,
      })
        .then((res) => {
          clearTimeout(timeoutId);
          if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
          return res.json();
        })
        .then((data) => {
          setMessages((prev) => [
            ...prev,
            {
              type: 'bot',
              text: data.response || 'Yanıt alınamadı.',
              timestamp: new Date(),
              webSearchAvailable: data.web_search_available,
              sources: data.sources,
              query: query,
            },
          ]);
          if (data.session_id && data.session_id !== sessionId) {
            setSessionId(data.session_id);
            try { sessionStorage.setItem('chat_session_id', data.session_id); } catch (e) {}
          }
        })
        .catch((error) => {
          let errorText = 'Üzgünüm, tekrar deneme başarısız oldu. Lütfen daha sonra tekrar deneyin.';
          if (error.name === 'AbortError') {
            errorText = 'İstek zaman aşımına uğradı.';
          } else if (error.message.includes('Failed to fetch')) {
            errorText = 'Sunucuya bağlanılamadı.';
          }
          setMessages((prev) => [
            ...prev,
            { type: 'bot', text: errorText, timestamp: new Date(), isError: true },
          ]);
        })
        .finally(() => setIsLoading(false));
    }, 0);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full p-4 shadow-2xl transition-all hover:scale-110"
          aria-label="Chat"
          data-testid="chatbot-button"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-50 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Ener Asistan</h3>
                <p className="text-xs text-white/80">Yapay Zeka Destekli</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/10 p-2 rounded-lg transition-colors"
                data-testid="chatbot-minimize"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  setMessages([
                    {
                      type: 'bot',
                      text: 'Merhaba! Ben Ener Otomasyon yapay zeka asistanıyım. Size nasıl yardımcı olabilirim?',
                      timestamp: new Date(),
                    },
                  ]);
                }}
                className="hover:bg-white/10 p-2 rounded-lg transition-colors"
                data-testid="chatbot-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div key={index}>
                <div
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`chat-message-${message.type}-${index}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.type === 'user'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                    
                    {/* Show sources if available */}
                    {message.sources && message.sources.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Kaynaklar:</p>
                        {message.sources.slice(0, 3).map((source, idx) => (
                          <p key={idx} className="text-xs text-gray-600">• {source}</p>
                        ))}
                      </div>
                    )}
                    
                    {/* Show web search badge if from web search */}
                    {message.fromWebSearch && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-blue-600">
                        <Globe className="w-3 h-3" />
                        <span>Web araması sonucu</span>
                      </div>
                    )}
                    
                    <p
                      className={`text-xs mt-1 ${
                        message.type === 'user' ? 'text-white/70' : 'text-gray-500'
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString('tr-TR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
                
                {/* Retry Button - Show on error messages */}
                {message.type === 'bot' && message.isError && message.canRetry && message.retryQuery && (
                  <div className="flex justify-start mt-2 ml-2">
                    <button
                      type="button"
                      onClick={() => handleRetry(message.retryQuery)}
                      disabled={isLoading || cooldownSeconds > 0}
                      className="flex items-center gap-2 px-3 py-2 text-xs bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className="w-3 h-3" />
                      <span>Tekrar Dene</span>
                    </button>
                  </div>
                )}

                {/* Web Search Button - Show if available and not already used */}
                {message.type === 'bot' && message.webSearchAvailable && !message.fromWebSearch && (
                  <div className="flex justify-start mt-2 ml-2">
                    <button
                      type="button"
                      onClick={() => handleWebSearch(message.query)}
                      disabled={isLoading}
                      className="flex items-center gap-2 px-3 py-2 text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Globe className="w-3 h-3" />
                      <span>Daha fazla bilgi için web'de ara</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-200 rounded-2xl px-4 py-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={cooldownSeconds > 0 ? `Lütfen ${cooldownSeconds} saniye bekleyin...` : "Mesajınızı yazın..."}
                disabled={isLoading || cooldownSeconds > 0}
                className="flex-1"
                data-testid="chatbot-input"
              />
              <Button
                type="button"
                onClick={handleSend}
                disabled={isLoading || !inputMessage.trim() || cooldownSeconds > 0}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                data-testid="chatbot-send"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;